import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { useFamily } from '../hooks/useFamily';
import { 
  FilterSettings,
  GroupingType,
  TaskGroup,
  Task
} from '../types';
import { StorageService } from '../utils/storage';

// ウェディンググループ情報の型定義
interface WeddingGroup {
  id: string;
  familyName: string;
  groupName?: string; // ファミリーグループとの互換性のため
  settings: {
    weddingDate: string;
    venue: string;
    budget: number;
    estimatedGuests: number;
  };
  members: Array<{
    userId: string;
    email: string;
    role: string;
    displayName: string;
    joinedAt: string;
  }>;
  inviteToken?: string;
  inviteLink?: string;
  createdAt: string;
}

// アプリケーション状態
interface WeddingAppState {
  // フィルター設定（LocalStorageに保存）
  filterSettings: FilterSettings;
  
  // 現在のビュー
  currentView: 'dashboard' | 'tasks' | 'add-task' | 'settings';
  
  // ローカルエラー（UI用）
  localError: string | null;
}

// Initial state
const loadInitialState = (): WeddingAppState => {
  const savedFilterSettings = StorageService.loadFilterSettings();

  return {
    filterSettings: savedFilterSettings || {
      category: 'all',
      status: 'all',
      assignee: 'all',
      period: 'all',
      grouping: 'period',
      sort: 'dueDateAsc',
      dateRangeStart: '',
      dateRangeEnd: '',
      hideCompleted: false
    },
    currentView: 'dashboard',
    localError: null
  };
};

// Action types
type Action =
  | { type: 'UPDATE_FILTER_SETTINGS'; payload: FilterSettings }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_CURRENT_VIEW'; payload: 'dashboard' | 'tasks' | 'add-task' | 'settings' }
  | { type: 'SET_LOCAL_ERROR'; payload: string | null };

// Reducer
const weddingAppReducer = (state: WeddingAppState, action: Action): WeddingAppState => {
  switch (action.type) {
    case 'UPDATE_FILTER_SETTINGS':
      return {
        ...state,
        filterSettings: action.payload
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        filterSettings: {
          category: 'all',
          status: 'all',
          assignee: 'all',
          period: 'all',
          grouping: 'period',
          sort: 'dueDateAsc',
          dateRangeStart: '',
          dateRangeEnd: '',
          hideCompleted: false
        }
      };
    case 'SET_CURRENT_VIEW':
      return {
        ...state,
        currentView: action.payload
      };
    case 'SET_LOCAL_ERROR':
      return {
        ...state,
        localError: action.payload
      };
    default:
      return state;
  }
};

// フィルター・ソート機能
const filterAndSortTasks = (tasks: Task[], filterSettings: FilterSettings): Task[] => {
  console.log('filterAndSortTasks: Starting with tasks:', tasks.length);
  console.log('filterAndSortTasks: Filter settings:', filterSettings);
  
  let filteredTasks = [...tasks];

  // カテゴリフィルター
  if (filterSettings.category !== 'all') {
    filteredTasks = filteredTasks.filter(task => task.category === filterSettings.category);
    console.log('filterAndSortTasks: After category filter:', filteredTasks.length);
  }

  // ステータスフィルター
  if (filterSettings.status !== 'all') {
    filteredTasks = filteredTasks.filter(task => task.status === filterSettings.status);
    console.log('filterAndSortTasks: After status filter:', filteredTasks.length);
  }

  // 完了タスクを隠すフィルター
  if (filterSettings.hideCompleted) {
    console.log('filterAndSortTasks: Hiding completed tasks. Before filter:', filteredTasks.length);
    console.log('filterAndSortTasks: Task statuses before filter:', filteredTasks.map(t => ({ id: t.id, name: t.name, status: t.status })));
    filteredTasks = filteredTasks.filter(task => task.status !== '完了');
    console.log('filterAndSortTasks: After hideCompleted filter:', filteredTasks.length);
    console.log('filterAndSortTasks: Remaining tasks:', filteredTasks.map(t => ({ id: t.id, name: t.name, status: t.status })));
  }

  // 担当者フィルター
  if (filterSettings.assignee !== 'all') {
    if (filterSettings.assignee === '未設定') {
      filteredTasks = filteredTasks.filter(task => 
        task.assignee === '' || task.assignee === undefined || task.assignee === null
      );
    } else {
      filteredTasks = filteredTasks.filter(task => task.assignee === filterSettings.assignee);
    }
    console.log('filterAndSortTasks: After assignee filter:', filteredTasks.length);
  }

  // 期間フィルター
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (filterSettings.period !== 'all') {
    switch (filterSettings.period) {
      case 'overdue':
        filteredTasks = filteredTasks.filter(task => {
          if (!task.dueDate || task.status === '完了') return false;
          const dueDate = new Date(task.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate < today;
        });
        break;
      case 'today':
        filteredTasks = filteredTasks.filter(task => {
          if (!task.dueDate) return false;
          const dueDate = new Date(task.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate.getTime() === today.getTime();
        });
        break;
      case 'this_week':
        const weekEnd = new Date(today);
        weekEnd.setDate(today.getDate() + (7 - today.getDay()));
        filteredTasks = filteredTasks.filter(task => {
          if (!task.dueDate) return false;
          const dueDate = new Date(task.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate >= today && dueDate <= weekEnd;
        });
        break;
      case 'this_month':
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        filteredTasks = filteredTasks.filter(task => {
          if (!task.dueDate) return false;
          const dueDate = new Date(task.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate >= today && dueDate <= monthEnd;
        });
        break;
      case 'next_month':
        const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const nextMonthEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        filteredTasks = filteredTasks.filter(task => {
          if (!task.dueDate) return false;
          const dueDate = new Date(task.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate >= nextMonthStart && dueDate <= nextMonthEnd;
        });
        break;
      case 'no_due_date':
        filteredTasks = filteredTasks.filter(task => !task.dueDate || task.dueDate === '');
        break;
      case 'custom_range':
        if (filterSettings.dateRangeStart && filterSettings.dateRangeEnd) {
          const startDate = new Date(filterSettings.dateRangeStart);
          const endDate = new Date(filterSettings.dateRangeEnd);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          filteredTasks = filteredTasks.filter(task => {
            if (!task.dueDate) return false;
            const dueDate = new Date(task.dueDate);
            return dueDate >= startDate && dueDate <= endDate;
          });
        }
        break;
    }
  }

  // ソート（要件書に従って日本語対応を強化）
  filteredTasks.sort((a, b) => {
    switch (filterSettings.sort) {
      case 'dueDateAsc':
        // 期限未設定のタスクは最後に表示
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      case 'dueDateDesc':
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
      case 'nameAsc':
        // 日本語のひらがな・カタカナ・漢字を適切に処理
        return a.name.localeCompare(b.name, 'ja', { numeric: true });
      case 'nameDesc':
        return b.name.localeCompare(a.name, 'ja', { numeric: true });
      case 'createdAtDesc':
        return (b.createdAt || 0) - (a.createdAt || 0);
      case 'createdAtAsc':
        return (a.createdAt || 0) - (b.createdAt || 0);
      case 'status':
        // ステータス順：未着手 → 進行中 → 完了
        const statusOrder = { '未着手': 0, '進行中': 1, '完了': 2 };
        const aOrder = statusOrder[a.status as keyof typeof statusOrder] ?? 3;
        const bOrder = statusOrder[b.status as keyof typeof statusOrder] ?? 3;
        return aOrder - bOrder;
      case 'assignee':
        // 担当者順：新郎 → 新婦 → 二人 → 未設定
        const assigneeOrder = { '新郎': 0, '新婦': 1, '二人': 2, '': 3 };
        const aAssigneeOrder = assigneeOrder[a.assignee as keyof typeof assigneeOrder] ?? 4;
        const bAssigneeOrder = assigneeOrder[b.assignee as keyof typeof assigneeOrder] ?? 4;
        if (aAssigneeOrder !== bAssigneeOrder) {
          return aAssigneeOrder - bAssigneeOrder;
        }
        // 同じ担当者の場合は名前順
        return a.name.localeCompare(b.name, 'ja', { numeric: true });
      case 'category':
        // カテゴリ順（日本語適切処理）
        const categoryComparison = a.category.localeCompare(b.category, 'ja', { numeric: true });
        if (categoryComparison !== 0) {
          return categoryComparison;
        }
        // 同じカテゴリの場合は名前順
        return a.name.localeCompare(b.name, 'ja', { numeric: true });
      default:
        return 0;
    }
  });

  return filteredTasks;
};

// グループ化表示機能（要件書F012に対応）
const groupTasks = (tasks: Task[], groupingType: GroupingType): TaskGroup[] => {
  if (groupingType === 'none') {
    return [{ title: '', tasks }];
  }

  const groups: { [key: string]: Task[] } = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  tasks.forEach(task => {
    let groupKey: string;

    switch (groupingType) {
      case 'period':
        if (!task.dueDate || task.dueDate === '') {
          groupKey = '期限未設定';
        } else {
          const dueDate = new Date(task.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          const diffTime = dueDate.getTime() - today.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays < 0) {
            groupKey = '期限切れ';
          } else if (diffDays === 0) {
            groupKey = '今日期限';
          } else if (diffDays <= 7) {
            groupKey = '今週期限';
          } else if (diffDays <= 30) {
            groupKey = '今月期限';
          } else if (diffDays <= 60) {
            groupKey = '来月期限';
          } else {
            groupKey = 'その他';
          }
        }
        break;

      case 'category':
        groupKey = task.category;
        break;

      case 'assignee':
        groupKey = task.assignee || '未設定';
        break;

      default:
        groupKey = 'その他';
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(task);
  });

  // グループの順序を要件に従って並び替え
  let sortedGroupKeys: string[];
  
  switch (groupingType) {
    case 'period':
      // 期間順：期限切れ → 今日期限 → 今週期限 → 今月期限 → 来月期限 → 期限未設定
      const periodOrder = ['期限切れ', '今日期限', '今週期限', '今月期限', '来月期限', 'その他', '期限未設定'];
      sortedGroupKeys = periodOrder.filter(key => groups[key]);
      break;
    
    case 'assignee':
      // 担当者順：新郎 → 新婦 → 二人 → 未設定
      const assigneeOrder = ['新郎', '新婦', '二人', '未設定'];
      sortedGroupKeys = assigneeOrder.filter(key => groups[key]);
      break;
    
    case 'category':
      // カテゴリ順：日本語順
      sortedGroupKeys = Object.keys(groups).sort((a, b) => 
        a.localeCompare(b, 'ja', { numeric: true })
      );
      break;
    
    default:
      sortedGroupKeys = Object.keys(groups);
  }

  return sortedGroupKeys.map(key => ({
    title: key,
    tasks: groups[key]
  }));
};

// Context type
interface WeddingContextType {
  // ローカル状態
  state: WeddingAppState;
  
  // ウェディング状態（useFamilyから）
  weddingGroup: WeddingGroup | null;
  hasWeddingGroup: boolean;
  isOwner: boolean;
  memberCount: number;
  isSoloMode: boolean;
  isCoupleMode: boolean;
  canInvite: boolean;
  tasks: Task[];
  settings: any;
  isLoading: boolean;
  isInitialized: boolean; // 初期化完了状態を追加
  error: string | null;
  
  // ウェディング操作
  createWeddingGroup: (familyId: string, familyName: string, weddingDate: string, venue: string, budget: number, estimatedGuests: number) => Promise<any>;
  createSimpleWeddingGroup: (familyId: string, familyName?: string) => Promise<any>;
  addTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateTask: (taskId: string, updateData: any) => Promise<void>;
  deleteTask: (taskId: string) => Promise<boolean>;
  updateSettings: (settings: any) => Promise<boolean>;
  generateInviteLink: () => Promise<any>;
  joinByInviteToken: (token: string) => Promise<any>;
  checkFamilyIdAvailability: (familyId: string) => Promise<boolean>;
  refreshFamilyGroup: () => Promise<any>;
  
  // ローカル操作
  updateFilterSettings: (settings: FilterSettings) => void;
  resetFilters: () => void;
  setCurrentView: (view: 'dashboard' | 'tasks' | 'add-task' | 'settings') => void;
  getFilteredTasks: () => Task[];
  getGroupedTasks: () => TaskGroup[];
  refreshTasks: () => Promise<void>;
  clearError: () => void;
  setLocalError: (error: string | null) => void;
  
  // ユーティリティ
  isAuthenticated: boolean;
  user: any;
}

// Create context
const WeddingContext = createContext<WeddingContextType | undefined>(undefined);

// Provider component
export const WeddingProvider: React.FC<{ children: ReactNode; user?: any }> = ({ children, user = null }) => {
  const [state, dispatch] = useReducer(weddingAppReducer, loadInitialState());
  
  // useFamilyフックを使用
  const {
    // 新設計のプロパティ（将来使用予定）
    // familyGroup,
    // hasFamilyGroup,
    // createFamilyGroup,
    
    // 互換性プロパティ
    weddingGroup,
    hasWeddingGroup,
    createWeddingGroup,
    createSimpleWeddingGroup,
    
    // 共通プロパティ
    isOwner,
    memberCount,
    isSoloMode,
    isCoupleMode,
    canInvite,
    tasks,
    settings,
    isLoading,
    isInitialized, // 初期化完了状態を取得
    error,
    addTask,
    updateTask,
    deleteTask,
    updateSettings,
    generateInviteLink: familyGenerateInviteLink,
    joinByInviteToken: familyJoinByInviteToken,
    checkFamilyIdAvailability,
    fetchTasks,
    fetchFamilyGroup,
    isAuthenticated
  } = useFamily(user);

  // デバッグログ: タスクデータの変化を監視（開発環境のみ、頻度制限）
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('WeddingContext: Tasks updated:', {
        taskCount: tasks.length,
        tasks: tasks.slice(0, 3), // 最初の3個のタスクを表示
        hasWeddingGroup,
        weddingGroup: weddingGroup ? {
          groupName: weddingGroup.groupName,
          familyName: weddingGroup.familyName
        } : null
      });
    }
  }, [tasks.length, hasWeddingGroup]); // weddingGroupオブジェクト全体ではなく、必要な値のみを監視

  // ローカル状態にフィルター設定を保存
  useEffect(() => {
    StorageService.saveFilterSettings(state.filterSettings);
  }, [state.filterSettings]);

  const updateFilterSettings = (settings: FilterSettings) => {
    console.log('updateFilterSettings: Updating filter settings:', settings);
    console.log('updateFilterSettings: Previous settings:', state.filterSettings);
    dispatch({ type: 'UPDATE_FILTER_SETTINGS', payload: settings });
  };

  const resetFilters = () => {
    dispatch({ type: 'RESET_FILTERS' });
  };

  const setCurrentView = (view: 'dashboard' | 'tasks' | 'add-task' | 'settings') => {
    dispatch({ type: 'SET_CURRENT_VIEW', payload: view });
  };

  const getFilteredTasks = () => {
    console.log('getFilteredTasks: Called with tasks:', tasks.length, 'filter settings:', state.filterSettings);
    const filtered = filterAndSortTasks(tasks, state.filterSettings);
    console.log('getFilteredTasks: Returning filtered tasks:', filtered.length);
    return filtered;
  };

  const getGroupedTasks = () => {
    console.log('getGroupedTasks: Called with grouping:', state.filterSettings.grouping);
    const filteredTasks = getFilteredTasks();
    console.log('getGroupedTasks: Using filtered tasks:', filteredTasks.length);
    const grouped = groupTasks(filteredTasks, state.filterSettings.grouping);
    console.log('getGroupedTasks: Returning grouped tasks:', grouped.length, 'groups');
    return grouped;
  };

  const refreshTasks = async () => {
    try {
      await fetchTasks();
    } catch (error) {
      console.error('Failed to refresh tasks:', error);
    }
  };

  const setLocalError = (error: string | null) => {
    dispatch({ type: 'SET_LOCAL_ERROR', payload: error });
  };

  const clearError = () => {
    setLocalError(null);
  };

  // ファミリーグループ再読み込み関数
  const refreshFamilyGroup = async () => {
    try {
      return await fetchFamilyGroup();
    } catch (error) {
      console.error('Failed to refresh family group:', error);
      throw error;
    }
  };

  // 統一された招待リンク生成関数（新設計を直接使用）
  const generateInviteLink = familyGenerateInviteLink;

  // 統一された招待による参加関数（新設計を直接使用）
  const joinByInviteToken = familyJoinByInviteToken;

  const value: WeddingContextType = {
    // ローカル状態
    state,
    
    // ウェディング状態
    weddingGroup,
    hasWeddingGroup,
    isOwner,
    memberCount,
    isSoloMode,
    isCoupleMode,
    canInvite,
    tasks,
    settings,
    isLoading,
    isInitialized,
    error: error || state.localError,
    
    // ウェディング操作
    createWeddingGroup,
    createSimpleWeddingGroup,
    addTask,
    updateTask,
    deleteTask,
    updateSettings,
    generateInviteLink,
    joinByInviteToken,
    checkFamilyIdAvailability,
    refreshFamilyGroup,
    
    // ローカル操作
    updateFilterSettings,
    resetFilters,
    setCurrentView,
    getFilteredTasks,
    getGroupedTasks,
    refreshTasks,
    clearError,
    setLocalError,
    
    // ユーティリティ
    isAuthenticated,
    user
  };

  return (
    <WeddingContext.Provider value={value}>
      {children}
    </WeddingContext.Provider>
  );
};

// Hook to use context
export const useWeddingContext = () => {
  const context = useContext(WeddingContext);
  if (context === undefined) {
    throw new Error('useWeddingContext must be used within a WeddingProvider');
  }
  return context;
}; 